import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { TICKET_STATUS, type TicketDetail } from '@his/shared';
import { put } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { humanize } from '@/lib/utils';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { Dialog, PageHeader } from '@/components/ui/layout';
import { Alert, Button, Field, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { TicketListView } from '../desk/TicketListView';
import { TicketThread } from '../desk/TicketThread';
import { OWNER_DESK } from '../desk/desk-api';

/**
 * Every ticket on the platform.
 *
 * The owner's distinct power here is moving a ticket to any state outright —
 * the contact person can only resolve or hand up. An optional note goes to the
 * hospital with the change, so a status never arrives unexplained.
 */
export function OwnerTicketsPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);
  const [changing, setChanging] = useState(false);
  const [status, setStatus] = useState('in_progress');
  const [note, setNote] = useState('');

  const change = useMutation({
    mutationFn: () => put<TicketDetail>(`/platform/tickets/${openId}/status`, { status, note: note.trim() || undefined }),
    onSuccess: (ticket) => {
      queryClient.setQueryData(['ticket', OWNER_DESK.tickets, ticket.id], ticket);
      void queryClient.invalidateQueries({ queryKey: ['tickets'] });
      toast.success(`Ticket ${ticket.number}: ${humanize(ticket.status, 'ticket_status')}`);
      setChanging(false);
      setNote('');
    },
    onError: (error) => toast.error('Could not change the status', describeError(error)),
  });

  if (openId) {
    return (
      <>
        <Button variant="ghost" className="mb-3" onClick={() => setOpenId(null)}>
          <ArrowLeft className="me-1.5 h-4 w-4" /> Back to tickets
        </Button>

        <TicketThread
          endpoints={OWNER_DESK}
          ticketId={openId}
          canWriteInternal
          actions={(ticket) => (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setStatus(ticket.status);
                setChanging(true);
              }}
            >
              Change status
            </Button>
          )}
        />

        <Dialog
          open={changing}
          onClose={() => setChanging(false)}
          title="Change the ticket status"
          footer={
            <>
              <Button variant="ghost" onClick={() => setChanging(false)}>
                Cancel
              </Button>
              <Button loading={change.isPending} onClick={() => change.mutate()}>
                Apply
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            <Field label="Status" required>
              <ChoiceButtons
                layout="chips"
                value={status}
                onChange={setStatus}
                options={TICKET_STATUS.map((value) => ({ value, label: humanize(value, 'ticket_status') }))}
              />
            </Field>
            <Field label="Note" hint="Optional. The facility sees this.">
              <Textarea rows={4} value={note} onChange={(e) => setNote(e.target.value)} />
            </Field>
          </div>
        </Dialog>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Tickets" description="Every facility, every ticket" />
      <Alert className="mb-4">
        Tickets marked <span className="font-medium">escalated</span> were handed up by an account
        manager who could not settle them — those are the ones waiting on you.
      </Alert>
      <TicketListView endpoints={OWNER_DESK} onOpen={(row) => setOpenId(row.id)} />
    </>
  );
}
